import { CSSProperties, ReactNode, Suspense } from "react";
import { createRoot } from "react-dom/client";

type EmbeddedRenderOptions = {
  parent: HTMLElement;
  shadowHostStyle?: CSSProperties;
  /**
   * 埋め込み先が用意したCSSのURL。Shadow DOM の中に最後に読み込むので、
   * ウィジェット側のスタイルを上書きできる
   */
  styleUrl?: string;
};

export default function embeddedRender(
  node: ReactNode,
  { parent, shadowHostStyle, styleUrl }: EmbeddedRenderOptions
) {
  // ホストWebサイトのCSSの影響を受けないように、Shadow DOM を作成し、その中にレンダリングする
  const shadowHost = parent.appendChild(document.createElement("div"));
  if (shadowHostStyle) {
    Object.assign(shadowHost.style, shadowHostStyle);
  }
  const shadowRoot = shadowHost.attachShadow({ mode: "open" });
  const container = shadowRoot.appendChild(document.createElement("div"));
  container.id = `omakase-voice-${Math.random().toString(36).slice(-8)}`;
  container.style.height = "100%";
  // 埋め込み先のWebサイトのテーマに関係なく、常にライトテーマを適用させる
  container.dataset["theme"] = "light";

  // スクリプトのURLからベースパスを取得
  const scriptElement = document.querySelector('script[src*="embedded"]');
  const scriptSrc = scriptElement?.getAttribute("src") || "";
  const basePath = scriptSrc
    .substring(0, scriptSrc.lastIndexOf("/") + 1)
    .replace("/scripts", "/styles");

  // Tailwind CSSを読み込む
  const linkElem = document.createElement("link");
  linkElem.setAttribute("rel", "stylesheet");
  linkElem.setAttribute("href", `${basePath}tailwind.css`);
  shadowRoot.appendChild(linkElem);

  // CSS変数をShadow DOM内に適用。
  //
  // Shadow DOM は埋め込み先のCSSを遮断するが、CSSカスタムプロパティだけは
  // 境界を越えて継承される。既定値を --showcan-* のフォールバックとして書くことで、
  // 埋め込み先が :root などで --showcan-* を指定すればここに効く。
  const styleElem = document.createElement("style");
  styleElem.textContent = `
    :host {
      --background: 0 0% 100%;
      --foreground: 20 14.3% 4.1%;
      --card: 0 0% 100%;
      --card-foreground: 20 14.3% 4.1%;
      --popover: 0 0% 100%;
      --popover-foreground: 20 14.3% 4.1%;
      --primary: 24 9.8% 10%;
      --primary-foreground: 60 9.1% 97.8%;
      --secondary: 60 4.8% 95.9%;
      --secondary-foreground: 24 9.8% 10%;
      --muted: 60 4.8% 95.9%;
      --muted-foreground: 25 5.3% 44.7%;
      --accent: 60 4.8% 95.9%;
      --accent-foreground: 24 9.8% 10%;
      --destructive: 0 84.2% 60.2%;
      --destructive-foreground: 60 9.1% 97.8%;
      --border: 20 5.9% 90%;
      --input: 20 5.9% 90%;
      --ring: 20 14.3% 4.1%;
      --radius: var(--showcan-radius, 0.5rem);
      font-family: var(--showcan-font-family, inherit);
      font-size: var(--showcan-font-size, 16px);
      line-height: var(--showcan-line-height, 1.5);
    }
    /*
     * ブランドカラーの段組み。中身は globals.css の :root と同じで、
     * .showcan-root を持たないウィジェット（音声エージェント）のために :host にも置く。
     * :host に置くのは --showcan-ink だけで、--showcan-brand は読むだけにしてある。
     * ここで --showcan-brand を定義すると、埋め込み先が :root に書いた値を潰してしまう
     */
    :host {
      --showcan-ink: var(--showcan-brand, var(--showcan-agent-brand, #e99bad));
      --showcan-ink-5: color-mix(in srgb, var(--showcan-ink) 5%, transparent);
      --showcan-ink-10: color-mix(in srgb, var(--showcan-ink) 10%, transparent);
      --showcan-ink-20: color-mix(in srgb, var(--showcan-ink) 20%, transparent);
      --showcan-ink-30: color-mix(in srgb, var(--showcan-ink) 30%, transparent);
      --showcan-ink-40: color-mix(in srgb, var(--showcan-ink) 40%, transparent);
      --showcan-ink-strong: color-mix(in srgb, var(--showcan-ink) 70%, #1e293b);
    }
    /* color-mix が無いブラウザでは薄い色だけ従来のピンクに戻す（globals.css と同じ退避） */
    @supports not (color: color-mix(in srgb, red 50%, transparent)) {
      :host {
        --showcan-ink-5: rgba(233, 155, 173, 0.05);
        --showcan-ink-10: rgba(233, 155, 173, 0.1);
        --showcan-ink-20: rgba(233, 155, 173, 0.2);
        --showcan-ink-30: rgba(233, 155, 173, 0.3);
        --showcan-ink-40: rgba(233, 155, 173, 0.4);
        --showcan-ink-strong: #c2708a;
      }
    }
    * {
      box-sizing: border-box;
    }
    :host * {
      font-size: inherit;
    }

    /*
     * 埋め込み先から差し替えられる項目。
     * 変数を指定しなければ、これまでと同じ見た目のままになる。
     */
    .showcan-launcher {
      background: var(--showcan-launcher-bg, #fff);
      z-index: var(--showcan-z-index, 50);
    }
    .showcan-panel {
      background: var(--showcan-panel-bg, #fff);
      border-radius: var(--showcan-panel-radius, 1.5rem);
    }
    .showcan-bubble--assistant {
      background: var(--showcan-assistant-bubble-bg, var(--showcan-ink-10));
      color: var(--showcan-assistant-bubble-color, #334155);
    }
    .showcan-bubble--user {
      background: var(--showcan-user-bubble-bg, var(--showcan-ink));
      color: var(--showcan-user-bubble-color, #fff);
    }
    .showcan-product-link {
      border-color: var(--showcan-product-link-border, var(--showcan-ink-40));
      color: var(--showcan-product-link-color, var(--showcan-ink-strong));
    }
    .showcan-header {
      background: var(--showcan-header-bg, transparent);
    }
    .showcan-composer {
      background: var(--showcan-composer-bg, transparent);
    }
    .showcan-send,
    .showcan-mic[data-recording="true"] {
      background: var(--showcan-accent, var(--showcan-ink));
      color: var(--showcan-accent-color, #fff);
    }
    .showcan-input:focus {
      border-color: var(--showcan-accent, var(--showcan-ink));
    }
  `;
  shadowRoot.appendChild(styleElem);

  // フォントを追加（一度だけ）
  if (!document.getElementById("omakase-voice-font")) {
    const link = document.head.appendChild(document.createElement("link"));
    link.id = "omakase-voice-font";
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = `https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;400;500;700&family=Noto+Serif+JP:wght@500;600&display=swap`;
  }

  // 埋め込み先が用意したCSSは最後に読み込む。
  // 同じ詳細度ならこちらが勝つので、変数で足りない調整はここで当てられる
  if (styleUrl) {
    const customStyle = document.createElement("link");
    customStyle.setAttribute("rel", "stylesheet");
    customStyle.setAttribute("href", styleUrl);
    shadowRoot.appendChild(customStyle);
  }

  // モーダル用のコンテナを追加
  const modalContainer = shadowRoot.appendChild(document.createElement("div"));
  modalContainer.id = "modal-container";

  createRoot(container).render(<Suspense fallback={null}>{node}</Suspense>);
}
